import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import api from 'services/api';
import {
  signInSuccess,
  signFailure,
  companyInformation,
  userAvatar,
  putPendentAttendance,
  changePasswordSuccess,
  changePasswordError,
  adminPermissionsSuccess,
} from './actions';
import history from 'services/history';
import jwt from 'jwt-decode';
import socketIOClient from 'socket.io-client';
import { useDispatch } from 'react-redux';
import PublicAccessData from 'services/dataService/PublicAccessData';
import PublicCpfAccessData from 'services/dataService/PublicCpfAccessData';

const ADMIN_TYPE = 1;
const MIN_REFRESH_INTERVAL = 60 * 1000;
let lastPermissionsRefresh = 0;

function* fetchAdminPermissions() {
  lastPermissionsRefresh = Date.now();
  try {
    const { data } = yield call(api.get, 'admin-permissions/me');
    yield put(
      adminPermissionsSuccess(
        data.is_super_admin,
        data.restricted,
        data.permissions || {}
      )
    );
  } catch (err) {
    console.error(err);
  }
}

export function* refreshAdminPermissions({ payload }) {
  const { token, type } = yield select((state) => state.auth);
  if (!token || type !== ADMIN_TYPE) return;

  const elapsed = Date.now() - lastPermissionsRefresh;
  if (!payload?.force && elapsed < MIN_REFRESH_INTERVAL) return;

  yield call(fetchAdminPermissions);
}

function* hydrateSession(response, email, redirectTo) {
  const { token, idAdminUser, idStudentUser, userStudent } = response.data;

  api.defaults.headers.Authorization = `Bearer ${token}`;

  const {
    id_company,
    exp,
    group,
    type,
    name,
    id,
    avatar,
    company_logo,
    company_name,
    shouldChangePassword,
  } = jwt(token);

  const responsePendentAttendanec = yield call(
    api.get,
    'form-contact/status'
  );

  const { pendentAttendance } = responsePendentAttendanec.data;

  yield put(
    signInSuccess(
      token,
      { type, group, name, id, email: email || userStudent?.email },
      idAdminUser,
      idStudentUser,
      shouldChangePassword
    )
  );
  yield put(companyInformation(id_company, exp, company_logo, company_name));
  yield put(userAvatar(avatar));
  yield put(putPendentAttendance(pendentAttendance));
  if (type === ADMIN_TYPE) {
    yield call(fetchAdminPermissions);
  }
  history.push(redirectTo);
  window.location.reload()
}

export function* signIn({ payload }) {
  try {
    const { email, password, domain, forceIdUser } = payload;

    const response = yield call(api.post, 'sessions', {
      email,
      password,
      domain,
      forceIdUser,
    });

    yield call(hydrateSession, response, email, '/dashboard');
  } catch (err) {
    console.error(err);
    if (!err?.errorCallback === true) {
      toast.error('Falha na autenticação, verifique seus dados');
    }
    yield put(signFailure());
  }
}

export function* publicAccessSignIn({ payload }) {
  try {
    const { companyCourseId, cpf, domain } = payload;
    const response = yield call(PublicAccessData.auth, companyCourseId, cpf, domain);

    yield call(
      hydrateSession,
      response,
      response.data?.userStudent?.email,
      `/course/${response.data?.companyCourse}/learn/${response.data?.firstLessonId}`
    );
  } catch (err) {
    console.error(err);
    toast.error('Não foi possível validar o acesso.');
    yield put(signFailure());
  }
}

export function* publicCpfSignIn({ payload }) {
  try {
    const { companyKey, cpf, domain } = payload;
    const response = yield call(PublicCpfAccessData.auth, companyKey, cpf, domain);

    yield call(
      hydrateSession,
      response,
      response.data?.userStudent?.email,
      '/dashboard'
    );
  } catch (err) {
    console.error(err);
    toast.error('Não foi possível validar o acesso.');
    yield put(signFailure());
  }
}

export function* changePassword({ payload }) {
  try {
    const { password, passwordConfirmation, domain } = payload;

    const response = yield call(api.put, 'user/change-password', {
      password,
      passwordConfirmation,
      domain,
    });

    const { token } = response.data;

    api.defaults.headers.Authorization = `Bearer ${token}`;

    const { group, type, name, id, email, shouldChangePassword } = jwt(token);

    yield put(
      changePasswordSuccess(
        token,
        { type, group, name, id, email },
        shouldChangePassword
      )
    );
    toast.success('Senha alterada com sucesso.');
    history.push('/dashboard');
  } catch (err) {
    if (!err?.errorCallback === true) {
      if (
        err?.response?.data?.message ===
        'Password and confirmation are not equals.'
      ) {
        toast.error('Senha e confirmação não conferem.');
      } else if (
        err?.response?.data?.message === 'You should change password.'
      ) {
        toast.error(
          'A nova senha deve ser diferente da atual.'
        );
      } else {
        toast.error(
          'Falha ao alterar senha, confirme os dados e tente novamente.'
        );
      }
    }
    yield put(changePasswordError());
  }
}

export default all([
  takeLatest('@auth/SIGN_IN_REQUEST', signIn),
  takeLatest('@auth/PUBLIC_ACCESS_SIGN_IN_REQUEST', publicAccessSignIn),
  takeLatest('@auth/PUBLIC_CPF_SIGN_IN_REQUEST', publicCpfSignIn),
  takeLatest('@auth/CHANGE_PASSWORD_REQUEST', changePassword),
  takeLatest('@auth/ADMIN_PERMISSIONS_REQUEST', refreshAdminPermissions),
]);
